import * as React from "react"
import { Head, Link, router, useForm } from "@inertiajs/react"
import { AppShell } from "@/components/AppShell"
import { PageHeader } from "@/components/PageHeader"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Select } from "@/components/ui/select"


type Workflow = {
  id: number
  name: string
  description: string | null
  enabled: boolean
  status: string
  graph_state_label: string
  graph_draft_different_from_published: boolean
  version: number
  published_version: number | null
  trigger_type: string
  trigger_label: string
  execution_mode: string
  execution_mode_label: string
  conditions_json: string
  actions_json: string
  conditions_summary: string
  action_summary: string
  last_run_at: string | null
  last_scheduled_run_at: string | null
  schedule_interval_minutes: number | null
  max_leads_per_run: number
  scheduler_summary: string
  scheduler_warning: string
  scheduler_due: boolean
  next_scheduled_run_at: string | null
  created_by_email: string | null
  updated_by_email: string | null
  recent_run_count: number
  applied_run_count: number
  last_run_status: string | null
  last_run_summary: string | null
}

type StageOption = { id: number; key: string; name: string }

type Props = {
  workflows: Workflow[]
  workflow: {
    id?: number
    name: string
    description: string
    enabled: boolean
    trigger_type: string
    execution_mode: string
    schedule_interval_minutes: string | number | null
    max_leads_per_run: string | number | null
    conditions_json: string
    actions_json: string
  }
  workflow_errors: string[]
  flash_notice: string | null
  trigger_types: string[]
  execution_modes: string[]
  stage_options: StageOption[]
  action_types: string[]
}

export default function WorkflowsIndex({ workflows, workflow, workflow_errors, flash_notice, trigger_types, execution_modes, stage_options, action_types }: Props) {
  const form = useForm({
    workflow: {
      ...workflow,
      schedule_interval_minutes: workflow.schedule_interval_minutes?.toString() ?? "",
      max_leads_per_run: workflow.max_leads_per_run?.toString() ?? "25",
    },
  })

  const submit = (event: React.FormEvent) => {
    event.preventDefault()
    form.post("/workflows", { preserveScroll: true })
  }

  return (
    <>
      <Head title="Workflows | LeadFlow">
        <meta name="description" content="Rule-based workflow automation for LeadFlow." />
        <meta property="og:title" content="Workflows | LeadFlow" />
        <meta property="og:description" content="Rule-based workflow automation for LeadFlow." />
      </Head>
      <AppShell wide>
        <PageHeader
          title="Workflows"
          description="Simple rule-based automation for staged lead follow-up and reminders."
          actions={<Button asChild variant="secondary"><Link href="/admin/status">Admin status</Link></Button>}
        />

        {flash_notice && <p className="mt-6 rounded-md border border-hairline bg-page px-3 py-2 text-sm text-ink-body">{flash_notice}</p>}
        {workflow_errors.length > 0 && <ErrorBox errors={workflow_errors} />}

        <div className="mt-6 grid gap-6 xl:grid-cols-[1.25fr_0.9fr]">
          <section className="rounded-lg border border-hairline bg-surface p-5">
            <div className="flex items-center justify-between gap-3">
              <h2>Workflows</h2>
              <Badge tone="muted">{workflows.length} total</Badge>
            </div>
            <div className="mt-4 space-y-4">
              {workflows.length === 0 && <p className="text-sm text-ink-muted">No workflows yet.</p>}
              {workflows.map((item) => (
                <article key={item.id} className="rounded-lg border border-hairline bg-page p-4">
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <Link href={`/workflows/${item.id}`} className="font-semibold text-ink-display">{item.name}</Link>
                        <Badge tone={item.enabled ? "accent" : "muted"}>{item.enabled ? "Enabled" : "Disabled"}</Badge>
                        <Badge tone={item.status === "published" ? "accent" : item.status === "inactive" ? "signal" : "muted"}>{humanize(item.status)}</Badge>
                        <Badge tone={item.graph_draft_different_from_published ? "signal" : "muted"}>{item.graph_draft_different_from_published ? "Draft changes" : item.graph_state_label}</Badge>
                        <Badge tone="neutral">{item.trigger_label}</Badge>
                        <Badge tone={item.execution_mode === "scheduled" && item.scheduler_due ? "accent" : item.execution_mode === "paused" ? "signal" : "muted"}>{item.execution_mode_label}</Badge>
                      </div>
                      {item.description && <p className="mt-1 text-sm text-ink-muted">{item.description}</p>}
                      <p className="mt-2 text-xs text-ink-muted">{item.conditions_summary || "No conditions configured"}</p>
                      <p className="mt-1 text-xs text-ink-muted">{item.action_summary || "No actions configured"}</p>
                      <p className="mt-1 text-xs text-ink-muted">{item.scheduler_summary}</p>
                      <p className="mt-1 text-xs text-ink-muted">Last run: {item.last_run_at || "None"}</p>
                      <p className="mt-1 text-xs text-ink-muted">Run a dry-run on the workflow page before applying.</p>
                    </div>
                    <div className="flex flex-wrap items-center gap-2">
                      <Button asChild variant="secondary" size="sm"><Link href={`/workflows/${item.id}`}>View</Link></Button>
                      <Button type="button" variant="secondary" size="sm" onClick={() => router.patch(`/workflows/${item.id}/toggle`, {}, { preserveScroll: true })}>{item.enabled ? "Disable" : "Enable"}</Button>
                      <Button type="button" variant="secondary" size="sm" onClick={() => router.post(`/workflows/${item.id}/dry_run`, {}, { preserveScroll: true })}>Dry run</Button>
                      <Button asChild variant="secondary" size="sm"><Link href={`/workflows/${item.id}`}>Review &amp; run</Link></Button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </section>

          <section className="rounded-lg border border-hairline bg-page p-5">
            <h2>New workflow</h2>
            <p className="mt-1 text-sm text-ink-muted">Keep the definition simple. Conditions and actions are JSON so the initial version stays flexible without a canvas editor.</p>
            <form className="mt-4 grid gap-4" onSubmit={submit}>
              <label className="grid gap-2">Name<Input required value={form.data.workflow.name} onChange={(event) => form.setData("workflow", { ...form.data.workflow, name: event.target.value })} /></label>
              <label className="grid gap-2">Description<textarea className="form-control form-control-textarea" value={form.data.workflow.description} onChange={(event) => form.setData("workflow", { ...form.data.workflow, description: event.target.value })} /></label>
              <label className="grid gap-2">Trigger<Select value={form.data.workflow.trigger_type} onChange={(event) => form.setData("workflow", { ...form.data.workflow, trigger_type: event.target.value })}>{trigger_types.map((trigger) => <option key={trigger} value={trigger}>{humanize(trigger)}</option>)}</Select></label>
              <label className="grid gap-2">Execution mode<Select value={form.data.workflow.execution_mode} onChange={(event) => form.setData("workflow", { ...form.data.workflow, execution_mode: event.target.value })}>{execution_modes.map((mode) => <option key={mode} value={mode}>{humanize(mode)}</option>)}</Select></label>
              <div className="grid gap-4 sm:grid-cols-2">
                <label className="grid gap-2">Schedule interval minutes<Input type="number" min="1" value={form.data.workflow.schedule_interval_minutes} onChange={(event) => form.setData("workflow", { ...form.data.workflow, schedule_interval_minutes: event.target.value })} placeholder="Only used when scheduled" /></label>
                <label className="grid gap-2">Max leads per run<Input type="number" min="1" value={form.data.workflow.max_leads_per_run} onChange={(event) => form.setData("workflow", { ...form.data.workflow, max_leads_per_run: event.target.value })} /></label>
              </div>
              <label className="flex items-center gap-2 text-sm font-normal text-ink-body"><input type="checkbox" checked={form.data.workflow.enabled} onChange={(event) => form.setData("workflow", { ...form.data.workflow, enabled: event.target.checked })} />Enabled</label>
              <label className="grid gap-2">Conditions JSON<textarea className="form-control form-control-textarea min-h-[11rem] font-mono text-xs" value={form.data.workflow.conditions_json} onChange={(event) => form.setData("workflow", { ...form.data.workflow, conditions_json: event.target.value })} placeholder={conditionsPlaceholder} /></label>
              <label className="grid gap-2">Actions JSON<textarea className="form-control form-control-textarea min-h-[12rem] font-mono text-xs" value={form.data.workflow.actions_json} onChange={(event) => form.setData("workflow", { ...form.data.workflow, actions_json: event.target.value })} placeholder={actionsPlaceholder} /></label>
              <div className="rounded-md border border-hairline bg-surface p-4 text-xs text-ink-muted">
                <p className="font-medium text-ink-display">Helpers</p>
                <p className="mt-1">Stage keys: {stage_options.map((stage) => stage.key).join(", ")}</p>
                <p className="mt-1">Supported actions: {action_types.join(", ")}</p>
                <p className="mt-1">CTM SMS is reserved for later and is not active in this pass.</p>
              </div>
              <Button disabled={form.processing}>Create workflow</Button>
            </form>
          </section>
        </div>
      </AppShell>
    </>
  )
}

function ErrorBox({ errors }: { errors: string[] }) {
  return <div className="mt-6 rounded-lg border border-danger/30 bg-danger/10 p-4 text-sm text-danger-display"><p className="font-medium">Please fix the workflow form:</p><ul className="mt-2 list-disc space-y-1 pl-5">{errors.map((error) => <li key={error}>{error}</li>)}</ul></div>
}

function humanize(value: string) {
  return value.replace(/_/g, " ").replace(/\b\w/g, (letter) => letter.toUpperCase())
}

const conditionsPlaceholder = `{
  "stage_keys": ["new_lead"],
  "minimum_minutes": 30,
  "repeat_window_minutes": 60
}`

const actionsPlaceholder = `[
  { "type": "move_stage", "to_stage_key": "in_progress" },
  { "type": "create_internal_note", "body": "Workflow note", "category": "internal_alert", "pinned": true }
]`
